import { useEffect, useState } from "react";
import { getAuthHeader } from "../../../utils/getAuthHeader";
import SideDrawer from "../SideDrawer";
import { API_BASE_ADMIN, MappingRow, showErrorToast } from "./types";

interface Props {
  row: MappingRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditDrawer({ row, onClose, onSaved }: Props) {
  const [deviceId, setDeviceId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) setDeviceId(row.device_id);
  }, [row]);

  if (!row) return null;

  const save = async () => {
    if (!deviceId.trim()) { showErrorToast("Device ID is required."); return; }
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_ADMIN}/iot_endpoints/iot-farm-mappings/${row.farm_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          body: JSON.stringify({ id: row.id, device_id: deviceId.trim() }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as any).message || `Request failed (${res.status})`);
      onSaved();
      onClose();
    } catch (err: any) {
      showErrorToast(err.message || "Update failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SideDrawer
      title="Edit Mapping"
      subtitle={`Farm ID: ${row.farm_id}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn d-flex align-items-center gap-2"
            style={{ backgroundColor: "#6D9300", color: "white", borderColor: "#6D9300" }}
            onClick={save}
            disabled={saving}
          >
            {saving ? (
              <><span className="spinner-border spinner-border-sm" /> Saving…</>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Update Mapping
              </>
            )}
          </button>
        </>
      }
    >
      <div className="mb-4">
        <label className="form-label fw-semibold" style={{ fontSize: "0.82rem" }}>Farm ID</label>
        <input type="text" className="form-control bg-light" value={row.farm_id} readOnly />
        <div className="form-text">Farm ID cannot be changed.</div>
      </div>
      <div className="mb-4">
        <label className="form-label fw-semibold" style={{ fontSize: "0.82rem" }}>Device ID</label>
        <input
          type="text"
          className="form-control"
          placeholder="Enter new Device ID"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
        />
        <div className="form-text">Only one device can be mapped per farm.</div>
      </div>
    </SideDrawer>
  );
}
